import { useEffect } from "react";

// Origin of the parent page that is allowed to embed TeamBuilder AI.
const PARENT_ORIGIN = "https://rendeavored.com";
const MESSAGE_TYPE = "TEAMBUILDER_RESIZE";

// Detect whether we are rendered inside an iframe. The comparison itself never
// throws even across origins; we still guard defensively.
function isEmbedded() {
  try {
    return window.self !== window.top;
  } catch {
    // Access was blocked -> we are definitely inside a cross-origin frame.
    return true;
  }
}

/**
 * Iframe-aware automatic height resizing.
 *
 * When embedded inside the Rendeavored parent page, this hook:
 *  - marks <html> with `rd-embedded` so CSS can stop forcing 100vh heights,
 *  - measures the full rendered document height, and
 *  - posts it to the parent using a strict target origin.
 *
 * A ResizeObserver + MutationObserver + debounced window handler ensure the
 * parent always receives an accurate height for every UI change (step changes,
 * advisor/insight cards, loading screens, results, expandable content, viewport
 * resize) without flooding it with messages.
 *
 * Standalone behavior (teambuilder.rendeavored.com) is untouched.
 */
export function useIframeResize() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isEmbedded()) return; // no-op when running standalone

    const root = document.documentElement;
    root.classList.add("rd-embedded");

    let frame = null;
    let timeout = null;
    let lastHeight = -1;

    const measure = () =>
      Math.ceil(
        Math.max(
          document.documentElement.scrollHeight,
          document.body ? document.body.scrollHeight : 0,
          document.documentElement.offsetHeight,
          document.body ? document.body.offsetHeight : 0
        )
      );

    const post = () => {
      const height = measure();
      if (height === lastHeight) return; // avoid excessive/duplicate messages
      lastHeight = height;
      try {
        window.parent.postMessage(
          { type: MESSAGE_TYPE, height },
          PARENT_ORIGIN
        );
      } catch {
        /* parent not reachable — ignore */
      }
    };

    // Debounce via rAF (coalesce within a frame) + short trailing timeout.
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      if (timeout) clearTimeout(timeout);
      frame = requestAnimationFrame(() => {
        post();
        // A trailing send catches late layout shifts (fonts, images, animations).
        timeout = setTimeout(post, 120);
      });
    };

    // Observe overall document size changes (content growth/shrink).
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(document.documentElement);
      if (document.body) resizeObserver.observe(document.body);
    }

    // Observe DOM changes (cards appearing, overlays, expandable content).
    const mutationObserver = new MutationObserver(schedule);
    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "hidden", "aria-expanded", "data-state"],
      });
    }

    // Viewport dimension changes.
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    // Re-measure once late-loading resources (fonts/images) settle.
    window.addEventListener("load", schedule);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule).catch(() => {});
    }

    // Initial sends: immediate + a few delayed to catch async first paint.
    schedule();
    const initialTimers = [150, 400, 900, 1600].map((ms) =>
      setTimeout(post, ms)
    );

    return () => {
      root.classList.remove("rd-embedded");
      if (frame) cancelAnimationFrame(frame);
      if (timeout) clearTimeout(timeout);
      initialTimers.forEach(clearTimeout);
      if (resizeObserver) resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.removeEventListener("load", schedule);
    };
  }, []);
}

export default useIframeResize;
